"use client";

import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  FRONT_DOOR_FIELD_STYLES,
  FRONT_DOOR_HELP_CLASS,
  FRONT_DOOR_LINK_CLASS,
  FRONT_DOOR_PRIMARY_BUTTON_CLASS,
} from "@/app/(front-door)/_components/styles";
import { Button } from "@/app/_components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_components/ui/form";
import { Input } from "@/app/_components/ui/input";
import { PasswordInput } from "@/app/_components/ui/password-input";
import { MarbleLink as Link } from "@/app/_components/ui/marble-link";
import getLogger from "@/lib/logger";
import { AuthSchema, type TAuthSchema } from "@/lib/modules/auth/schemas";
import { SIGN_IN_LOCKED_ERROR } from "@/lib/modules/auth/sign-in-lockout/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";

const logger = getLogger("front-door:staff-sign-in");

/** Email and password, for everyone but families. */
export const StaffSignInForm = ({
  emailPlaceholder,
  callbackUrl,
}: {
  emailPlaceholder: string;
  callbackUrl?: string;
}) => {
  const router = useRouter();
  const form = useForm<TAuthSchema>({
    resolver: zodResolver(AuthSchema),
    defaultValues: { email: "", password: "" },
  });

  const signInMutation = useMutation({
    mutationFn: (values: TAuthSchema) =>
      signIn("credentials", { ...values, redirect: false }),
    onSuccess: (response) => {
      if (response?.ok) {
        router.push(callbackUrl ?? "/");
        router.refresh();
        return;
      }
      if (response?.error === SIGN_IN_LOCKED_ERROR) {
        form.setError("root", { type: "custom", message: response.error });
      } else if (response?.error === "Invalid password") {
        form.setError("password", { type: "custom", message: response.error });
      } else if (response?.error === "User not found") {
        form.setError("email", { type: "custom", message: response.error });
      } else {
        logger.error(response, "Error signing in user");
        form.setError("root", {
          type: "custom",
          message: "Something went wrong. Please reload and try again.",
        });
      }
    },
    onError: (error) => {
      logger.error(error, "Error signing in user");
      form.setError("root", {
        type: "custom",
        message: "Something went wrong. Please reload and try again.",
      });
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => signInMutation.mutate(values))}
        method="post"
        className={FRONT_DOOR_FIELD_STYLES}
      >
        {form.formState.errors.root && (
          <FrontDoorNotice tone="error" className="mb-[18px]">
            {form.formState.errors.root.message}
          </FrontDoorNotice>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  autoComplete="email"
                  placeholder={emailPlaceholder}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="mt-[18px]">
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput
                  {...field}
                  autoComplete="current-password"
                  placeholder="Password"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="mt-2.5 flex justify-end">
          <Link
            href="/auth/password-reset"
            className={`text-sm ${FRONT_DOOR_LINK_CLASS}`}
          >
            Forgot password?
          </Link>
        </div>
        <Button
          type="submit"
          variant="custom"
          className={`mt-[18px] ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
          loading={signInMutation.isPending || signInMutation.data?.ok}
        >
          Sign in
        </Button>
        <p className={`mt-3.5 text-center ${FRONT_DOOR_HELP_CLASS}`}>
          {"By signing in, you agree to our "}
          <Link
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={FRONT_DOOR_LINK_CLASS}
          >
            Terms of Service
          </Link>
          .
        </p>
      </form>
    </Form>
  );
};
